import * as userAction from "../actions/userActions";
import * as meActions from "../actions/meActions";
import { post, get } from "./axiosClient";
import * as requestActions from "../actions/requestActions";
import * as settingsActions from "../actions/settingsActions";
import { resolveLanguage } from "../utils/language";
import {
  canBypassMaintenance,
  isMaintenanceModeEnabled,
} from "../utils/maintenanceAccess";
import sha512 from "js-sha512";

let store;
let roomClient;
let messagingClient;
let intl;

export default class AuthService {
  static init(storeObj, messagingClientObj, roomClientObj, intlObj) {
    store = storeObj;
    roomClient = roomClientObj;
    messagingClient = messagingClientObj;
    intl = intlObj;
  }

  login(username, password, roomId) {
    store.dispatch(userAction.setLoginProgress(true));
    const language = resolveLanguage(store.getState().user.userLanguage);
    // password = plaintext; hash for local users, plainPassword for LDAP bind
    post(`/auth/`, {
      username: username,
      password: sha512(password),
      plainPassword: password,
      lang: language,
    })
      .then((response) => {
        if (response.status === 200) {
          if (response.data.error === "INCORRECT_USERNAME_OR_PASSWORD") {
            store.dispatch(
              requestActions.notify({
                type: "error",
                text: intl.formatMessage({
                  id: "user.wrongUsernameOrPassword",
                  defaultMessage: "Wrong username or password",
                }),
              })
            );
          } else if (
            response.data.error === "FAILED" &&
            response.data.payload === "TOO_MANY_PASSWORD_ATTEMPTS"
          ) {
            store.dispatch(
              requestActions.notify({
                type: "error",
                text: intl.formatMessage({
                  id: "user.tooManyPasswordAttempts",
                  defaultMessage:
                    "Too many password attempts, please wait for a while",
                }),
              })
            );
          } else if (
            response.data.error === "FAILED" &&
            response.data.payload === "USER_DELETED"
          ) {
            store.dispatch(
              requestActions.notify({
                type: "error",
                text: intl.formatMessage({
                  id: "user.userDeleted",
                  defaultMessage: "This user is deleted.",
                }),
              })
            );
          } else if (
            response.data.error === "FAILED" &&
            response.data.payload === "MAINTENANCE_MODE"
          ) {
            store.dispatch(
              requestActions.notify({
                type: "error",
                text: intl.formatMessage({
                  id: "user.maintenanceMode",
                  defaultMessage:
                    "Sistem bakımdadır. Yalnızca root admin giriş yapabilir.",
                }),
              })
            );
          } else {
            const user = response.data.payload.user;
            if (
              isMaintenanceModeEnabled() &&
              !canBypassMaintenance(user)
            ) {
              store.dispatch(
                requestActions.notify({
                  type: "error",
                  text: intl.formatMessage({
                    id: "user.maintenanceMode",
                    defaultMessage:
                      "Sistem bakımdadır. Yalnızca root admin giriş yapabilir.",
                  }),
                })
              );
              store.dispatch(userAction.setLoginProgress(false));
              return;
            }
            if (!roomId)
              store.dispatch(meActions.setUIStep("MANAGEMENT_SCREEN"));
            localStorage.removeItem("token");
            localStorage.setItem("token", response.data.payload.token);
            try {
              localStorage.setItem("user", JSON.stringify(user));
            } catch (e) {
              /* ignore */
            }
            roomClient.setPeerId(user.id);
            store.dispatch(userAction.setUser(user));
            store.dispatch(
              settingsActions.setDisplayName(
                user.firstName + " " + user.lastName
              )
            );
            store.dispatch(meActions.addRole(user.meetingRole));
            store.dispatch(meActions.loggedIn(true));
            try {
              messagingClient.connect(user.presenceStatus);
            } catch (e) {
              /* messaging optional */
            }
            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "room.loggedIn",
                  defaultMessage: "You are logged in",
                }),
              })
            );
          }
        }
        store.dispatch(userAction.setLoginProgress(false));
      })
      .catch(() => {
        store.dispatch(meActions.loggedIn(false));
        store.dispatch(userAction.setLoginProgress(false));
      });
  }

  checkAuth(redirectToRoom) {
    store.dispatch(userAction.setLoginProgress(true));
    const existingToken = localStorage.getItem("token");
    const goLogin = () => {
      store.dispatch(meActions.loggedIn(false));
      if (!redirectToRoom) {
        store.dispatch(meActions.setUIStep("AUTHENTICATED_ENTRANCE_SCREEN"));
      }
    };
    const restoreCachedUser = () => {
      try {
        const cached = localStorage.getItem("user");
        if (!cached || !cached.startsWith("{")) return null;
        const user = JSON.parse(cached);
        if (!user || !user.id) return null;
        store.dispatch(userAction.setUser(user));
        store.dispatch(
          settingsActions.setDisplayName(
            `${user.firstName || ""} ${user.lastName || ""}`.trim()
          )
        );
        if (user.meetingRole) {
          store.dispatch(meActions.addRole(user.meetingRole));
        }
        try {
          roomClient.setPeerId(user.id);
        } catch (e) {
          /* ignore */
        }
        return user;
      } catch (e) {
        return null;
      }
    };

    get(`/auth/check`)
      .then((response) => {
        if (response.status === 200 && response.data && response.data.payload) {
          if (!redirectToRoom) {
            store.dispatch(meActions.setUIStep("MANAGEMENT_SCREEN"));
          }
          roomClient.setPeerId(response.data.payload.id);
          store.dispatch(meActions.loggedIn(true));
          try {
            localStorage.setItem(
              "user",
              JSON.stringify(response.data.payload)
            );
          } catch (e) {
            /* ignore */
          }
          store.dispatch(userAction.setUser(response.data.payload));
          store.dispatch(meActions.addRole(response.data.payload.meetingRole));
          try {
            messagingClient.connect(response.data.payload.presenceStatus);
          } catch (e) {
            /* messaging optional for portal shell */
          }
          store.dispatch(
            settingsActions.setDisplayName(
              response.data.payload.firstName +
                " " +
                response.data.payload.lastName
            )
          );
        } else if (response.status === 200 && existingToken) {
          // Token var ama payload yoksa cache ile devam et; yoksa login
          const user = restoreCachedUser();
          if (user) {
            store.dispatch(meActions.loggedIn(true));
            if (!redirectToRoom) {
              store.dispatch(meActions.setUIStep("MANAGEMENT_SCREEN"));
            }
          } else {
            localStorage.removeItem("token");
            goLogin();
          }
        } else {
          goLogin();
        }
        store.dispatch(userAction.setLoginProgress(false));
      })
      .catch((r) => {
        const status = r && r.response && r.response.status;
        // 401/403: oturum geçersiz → login
        if (status === 401 || status === 403) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          goLogin();
          store.dispatch(userAction.setLoginProgress(false));
          return;
        }
        // Ağ/TLS hatası: yalnızca geçerli cache user varsa oturumu koru
        if (existingToken && (!status || status >= 500)) {
          const user = restoreCachedUser();
          if (user) {
            store.dispatch(meActions.loggedIn(true));
            if (!redirectToRoom) {
              store.dispatch(meActions.setUIStep("MANAGEMENT_SCREEN"));
            }
          } else {
            localStorage.removeItem("token");
            goLogin();
          }
        } else {
          goLogin();
        }
        store.dispatch(userAction.setLoginProgress(false));
      });
  }
}
